'use client'
import { useRef, useEffect } from "react"
import Image from "next/image"
import { useLandingData } from "@/hooks/use-landing-data"

// Horizontal auto-sliding partners section, clickable, fix scrollbar
export function PartnersSection() {
  const { partners, loading } = useLandingData()
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll horizontally at 100px/sec, but only when not hovering or user scrolling
  useEffect(() => {
    if (loading || !partners?.length) return

    const container = scrollRef.current
    if (!container) return

    let frame: number
    const speed = 100 // px/sec

    let prevTimestamp: number | null = null
    let mouseOver = false
    let isUserScrolling = false

    // Pause auto-scroll when hovering or scrolling
    const handleMouseOver = () => { mouseOver = true }
    const handleMouseOut = () => { mouseOver = false }
    const handleScroll = () => {
      isUserScrolling = true
      clearTimeout((container as any)._scrollTimeout)
      ;(container as any)._scrollTimeout = setTimeout(() => { isUserScrolling = false }, 2)
    }

    container.addEventListener("mouseover", handleMouseOver)
    container.addEventListener("mouseout", handleMouseOut)
    container.addEventListener("scroll", handleScroll)

    const scrollStep = (timestamp: number) => {
      if (!container) return
      if (prevTimestamp === null) prevTimestamp = timestamp
      const dt = (timestamp - prevTimestamp) / 1000 // seconds elapsed
      prevTimestamp = timestamp

      // Only scroll if not paused
      if (!mouseOver && !isUserScrolling) {
        // Step size for this frame
        const step = speed * dt
        container.scrollLeft += step
        // Do NOT forcibly reset scrollLeft, let user scroll as far as possible (free scroll)
        // Remove "reset to start" for infinite scroll effect; only scroll as much as content allows
      }

      frame = requestAnimationFrame(scrollStep)
    }

    frame = requestAnimationFrame(scrollStep)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      container.removeEventListener("mouseover", handleMouseOver)
      container.removeEventListener("mouseout", handleMouseOut)
      container.removeEventListener("scroll", handleScroll)
    }
  }, [loading, partners])

  if (loading) {
    return (
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl   mb-6 py-2">
            Суралцаж буй сургуулиуд
          </h2>
          <p className="text-lg lg:text-xl text-muted-foreground">
            Манай оюутнууд дараах сургуулиудад өргөдөл илгээн амжилттай элсэж байна
          </p>
        </div>
          <div className="text-center mb-16">
            <div className="animate-pulse">
              <div className="h-12 bg-muted rounded w-1/2 mx-auto mb-6"></div>
              <div className="h-6 bg-muted rounded w-3/4 mx-auto"></div>
            </div>
          </div>
          <div className="flex gap-6 md:gap-8 overflow-x-auto scroll-smooth">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="animate-pulse flex flex-col items-center min-w-[160px]">
                <div className="bg-gray-200 rounded-2xl p-6 w-32 h-32"></div>
                <div className="h-4 bg-gray-200 rounded w-20 mx-auto mt-3"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // To make infinite loop effect, duplicate the partner logos once (optional)
  // But for "free scroll", do NOT forcibly reset scroll position
  const loopPartners = partners.length > 0
    ? [...partners, ...partners]
    : []

  return (
    <section className="py-20 px-6 overflow-hidden bg-transparent relative ">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl  mb-6 py-2">
            Суралцаж буй сургуулиуд
          </h2>
          <p className="text-lg lg:text-xl text-muted-foreground">
            Манай оюутнууд дараах сургуулиудад өргөдөл илгээн амжилттай элсэж байна
          </p>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-8 md:gap-12 overflow-x-auto no-scrollbar cursor-grab pb-8"
          tabIndex={0}
          style={{
            scrollBehavior: "auto",
            minHeight: "180px",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {loopPartners.map((partner, index) => (
            <div
              key={index}
              tabIndex={0}
              aria-label={partner.name}
              className="group flex-shrink-0 min-w-[150px] max-w-[180px] flex flex-col items-center transition-all duration-300 hover:shadow-xl rounded-2xl p-4 focus:outline-none focus:ring-2 focus:ring-primary"
              style={{
                background: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(2.5px)",
                borderRadius: "1.5rem",
                border: "1px solid hsl(var(--border))",
                textDecoration: "none",
              }}
            >
              <div className="w-32 h-32 rounded-3xl flex items-center justify-center overflow-hidden mb-2 bg-white/80">
                <Image
                  src={partner.logo}
                  alt={partner.name}
                  width={120}
                  height={120}
                  className="grayscale group-hover:grayscale-0 transition-all duration-300 filter drop-shadow-sm object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placekeanu.com/150/150'
                  }}
                />
              </div>
              <p className="text-center text-gray-700 mt-1 text-xs   group-hover:text-accent transition-colors whitespace-normal">
                {partner.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}